/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import RecallManager from '../Game/RecallManager';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import ReferencedComponent from '../Utility/Component/ComponentReference/ReferencedComponent';
import ObservableBooleanComponent from '../Utility/Component/Observable/ObservableBooleanComponent';
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import Decorators from '../Utility/Decorators';
import IObservable from '../Utility/Observable/IObservable';
import Observable from '../Utility/Observable/Observable';
import TypeStrings from '../Utility/TypeStrings';
import AudioCollection from './AudioCollection';
import AudioTrigger from './AudioTrigger';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * Manages an array of [[AudioCollection]]s and collates its [[AudioTrigger]]s
 * based on the collection's priority.
 */
@cc._decorator.ccclass
export default class AudioManager extends ReferencedComponent
{
    /** @hidden */
    protected _recallManagerRef: InjectableComponent<RecallManager> = new InjectableComponent<RecallManager>();

    @property({ type: ObservableBooleanComponent })
    protected isMuteObservable: ObservableBooleanComponent = null;

    /**
     * The array of [[AudioCollection]]s to manage.
     */
    @property({ type: [AudioCollection], visible: true })
    private _audioCollections: AudioCollection[] = [];

    /**
     * Collated collection of [[AudioTrigger]]s from the array of [[AudioCollection]]s.
     */
    private _triggers: Map<string, AudioTrigger> = new Map();

    protected volumeObservable: IObservable<number> = new Observable<number>(100);
    public set Volume(value: number) { this.volumeObservable.Value = value; }

    private _activeTriggers: AudioTrigger[] = [];

    /** @hidden */
    protected setContexts(): void
    {
        this._recallManagerRef.SetContext(RecallManager);
    }

    /** @hidden */
    protected start(): void
    {
        super.start();

        this.collateCollections();

        this.isMuteObservable.OnChanged(this.onMuteChanged);
        this.volumeObservable.OnChanged(this.onVolumeChangedHandler);

        this.Mute();
    }

    /** @hidden */
    protected onDestroy(): void
    {
        if (this.isMuteObservable) { this.isMuteObservable.OffChanged(this.onMuteChanged); }
        if (this.volumeObservable) { this.volumeObservable.OffChanged(this.onVolumeChangedHandler); }
        super.onDestroy();
    }

    /** Mute all audio collections. */
    public Mute(): AudioManager
    {
        this.isMuteObservable.Value = true;
        return this;
    }

    /** Unmuate all audio collection. */
    public UnMute(): AudioManager
    {
        this.isMuteObservable.Value = false;
        return this;
    }

    /** Toggle isMute [[Observable]] boolean value. */
    public MuteToggle(): AudioManager
    {
        if (this.isMuteObservable.Value)
        {
            this.UnMute();
        }
        else
        {
            this.Mute();
        }
        return this;
    }

    /** Event handler when mute observalbe changed. */
    @Decorators.BindThis()
    private onMuteChanged = (event: cc.Event.EventCustom): void =>
    {
        if (event.detail)
        {
            this._audioCollections.forEach(audioCollection => audioCollection.Mute());
        }
        else
        {
            this._audioCollections.forEach(audioCollection => audioCollection.UnMute());
        }
    }

    public SetVolume(toVolume: number): AudioManager
    {
        if (toVolume > 100) { toVolume = 100; }
        else if (toVolume < 0) { toVolume = 0; }

        this.volumeObservable.Value = toVolume;
        return this;
    }

    @Decorators.BindThis()
    private onVolumeChangedHandler(event: cc.Event.EventCustom): void
    {
        let toVolume: number = event.detail as number;

        if (toVolume === null
            || toVolume === undefined
            || typeof(toVolume) !== TypeStrings.number) { return; }

        toVolume = toVolume  / 100.0;
        this._audioCollections.forEach(collection => collection.SetVolume(toVolume));
    }

    /**
     * Compares two [[AudioCollection]]s based on priority.
     * @param a First [[AudioCollection]].
     * @param b Second [[AudioCollection]].
     * @returns -1 if a < b, 0 if equal, 1 if a > b.
     */
    private comparePriorities(a: AudioCollection, b: AudioCollection): number
    {
        if (a.Priority < b.Priority) { return -1; }
        if (a.Priority === b.Priority) { return 0; }
        if (a.Priority > b.Priority) { return 1; }
    }

    /**
     * Compiles the [[AudioTrigger]]s from each [[AudioCollection]] into a single collection.
     * Collisions of [[AudioTrigger]]s with identical cues are resolved by the priority of
     * the owning [[AudioCollection]].
     */
    private collateCollections(): void
    {
        this._audioCollections.sort(this.comparePriorities);
        for (let collection of this._audioCollections)
        {
            for (let trigger of collection.Triggers)
            {
                if (this._triggers.has(trigger.AudioTriggerID))
                {
                    CLog.warn(`Audio trigger already exists: [${trigger.AudioTriggerID}].`,
                        LogTypes.Initialization, this);
                    continue;
                }
                this._triggers[trigger.AudioTriggerID] = trigger;
            }
        }
    }

    /**
     * Plays the indicated sound from the collated collection of [[AudioTrigger]]s.
     * @param event (Optional) The triggering event or null.
     * @param customEventData The audio cue to lookup the trigger.
     */
    public TriggerSound(event: cc.Event.EventCustom, customEventData: string): void
    {
        if (!customEventData) { return; }

        const trigger: AudioTrigger = this._triggers[customEventData];
        if (trigger == null)
        {
            CLog.warn(`Could not trigger audio: [${customEventData}]`, LogTypes.Audio, this);
            return;
        }
        if (this._recallManagerRef.HasValue() && this._recallManagerRef.Value.Recalling) { return; }

        this._activeTriggers.push(trigger);
        trigger.OnComplete = () => this.removeActiveTrigger(trigger);
        trigger.Trigger();
    }

    /**
     * Stops the indicated sound from the collated collection of [[AudioTrigger]]s.
     * @param event (Optional) The triggering event or null.
     * @param customEventData The audio cue to lookup the trigger.
     */
    public StopTrigger(event: cc.Event.EventCustom, customEventData: string): void
    {
        if (!customEventData) { return; }

        let trigger: AudioTrigger = this._triggers[customEventData];
        if (trigger == null)
        {
            CLog.warn("Could not stop trigger audio: " + customEventData, LogTypes.Audio, this);
            return;
        }
        trigger.Stop();
    }

    public Pause(): void
    {
        this._activeTriggers.forEach(trigger => trigger.Pause());
    }

    public Resume(): void
    {
        this._activeTriggers.forEach(trigger => trigger.Resume());
    }

    private removeActiveTrigger(trigger: AudioTrigger): void
    {
        this._activeTriggers = this._activeTriggers.filter(activeTrigger => activeTrigger !== trigger);
    }
}
